import { useEffect, type RefObject } from 'react';
import { subscribeScroll } from '../lib/smoothScroll';

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

/** Deck reaches full height at 62% of headline scroll */
const LIFT_COMPLETE_AT = 0.62;

/** Spread completes quickly with minimal hold after align */
const DISPERSE_COMPLETE_AT = 0.72;

export function useHeroTwoScroll(sectionRef: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const applyVars = (lift: number, disperse: number) => {
      section.style.setProperty('--hero-two-progress', lift.toFixed(4));
      section.style.setProperty('--hero-two-disperse', disperse.toFixed(4));
    };

    if (reducedMotion) {
      applyVars(1, 1);
      return;
    }

    let frame = 0;
    let startHeadBottom = 0;

    const measureStart = () => {
      const head = section.querySelector('.hero-two__head-wrap') as HTMLElement | null;
      if (!head) return;
      const bottom = head.getBoundingClientRect().bottom;
      if (bottom > 0) startHeadBottom = bottom;
    };

    const update = () => {
      const head = section.querySelector('.hero-two__head-wrap') as HTMLElement | null;
      if (!head) return;

      if (window.scrollY < 8) measureStart();
      if (startHeadBottom <= 0) measureStart();
      if (startHeadBottom <= 0) return;

      const headBottom = head.getBoundingClientRect().bottom;
      const liftRaw = clamp(1 - headBottom / startHeadBottom, 0, 1);
      const lift = clamp(liftRaw / LIFT_COMPLETE_AT, 0, 1);

      let disperse = 0;
      if (lift >= 1) {
        const spacer = section.querySelector('.hero-two__scroll-spacer') as HTMLElement | null;
        if (spacer) {
          const spacerTop = spacer.getBoundingClientRect().top;
          const vh = window.innerHeight;
          const disperseRaw = clamp((vh - spacerTop) / vh, 0, 1);
          disperse = clamp(disperseRaw / DISPERSE_COMPLETE_AT, 0, 1);
        }
      }

      applyVars(lift, disperse);
    };

    const scheduleUpdate = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        update();
        frame = 0;
      });
    };

    measureStart();
    applyVars(0, 0);
    update();

    const unsubScroll = subscribeScroll(scheduleUpdate);
    window.addEventListener('resize', scheduleUpdate, { passive: true });

    return () => {
      unsubScroll();
      window.removeEventListener('resize', scheduleUpdate);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [sectionRef]);
}
